import { HttpInterceptorFn } from '@angular/common/http';
import { fetchAuthSession } from 'aws-amplify/auth';
import { from, switchMap } from 'rxjs';

/**
 * HTTP Interceptor for AWS Amplify Authentication
 *
 * Automatically attaches the JWT token from Cognito to all outgoing HTTP requests
 * that are made to the API backend. This ensures authenticated API calls work correctly.
 *
 * The interceptor:
 * 1. Fetches the current auth session from Amplify
 * 2. Extracts the ID token (JWT)
 * 3. Adds it as an Authorization header (Bearer token)
 * 4. Passes the request to the next handler
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  // Convert the async operation to an Observable using from()
  return from(
    fetchAuthSession()
      .then(session => {
        // Extract the ID token from the session
        const token = session.tokens?.idToken?.toString();

        // If we have a token, clone the request and add the Authorization header
        if (token) {
          const clonedRequest = req.clone({
            setHeaders: {
              Authorization: `Bearer ${token}`
            }
          });
          return clonedRequest;
        }

        // If no token, return the original request
        return req;
      })
      .catch(error => {
        // If there's an error fetching the session (e.g., user not logged in),
        // just return the original request without the auth header
        console.warn('Failed to fetch auth session:', error);
        return req;
      })
  ).pipe(
    // Pass the (possibly modified) request to the next handler
    switchMap(modifiedReq => next(modifiedReq))
  );
};
